import { Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useDocumentMeta } from "@/hooks/use-document-meta";
import { Workflow, Library as LibIcon, Users, PlayCircle, ArrowRight, Hexagon } from "lucide-react";

const HERO = "https://static.prod-images.emergentagent.com/jobs/abc48b77-7c54-4f71-8dd5-4220d773b6d6/images/c0511f2eefc6bf76bcf60bb82efddc6b3a3a977fcb23ae6d84212be21a7e66a3.png";

export default function Landing() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  useDocumentMeta(
    "Infinite Sequence — Map Your Brazilian Jiu-Jitsu Game",
    "Infinite Sequence is a Brazilian Jiu-Jitsu app to build a video technique library and map your game with a visual drag-and-drop sequence builder. Share with your gym. 7-day free trial."
  );
  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      {/* Nav */}
      <header className="fixed top-0 inset-x-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Hexagon className="h-6 w-6 text-primary fill-primary/20" strokeWidth={2.5} />
            <span className="font-heading font-extrabold text-lg tracking-tight">INFINITE SEQUENCE</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/how-it-works" className="hidden sm:block"><Button data-testid="nav-how-it-works-btn" variant="ghost" className="text-muted-foreground hover:text-white">How it works</Button></Link>
            <Link to="/login"><Button data-testid="nav-login-btn" variant="ghost" className="text-muted-foreground hover:text-white">Log in</Button></Link>
            <Link to="/register"><Button data-testid="nav-signup-btn" className="bg-primary hover:bg-primary/90 text-white rounded">Get Started</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-16 min-h-screen flex items-center">
        <div className="absolute inset-0">
          <img src={HERO} alt="Brazilian Jiu-Jitsu" className="w-full h-full object-cover brightness-125" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/45 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-transparent to-background/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-24 w-full">
          <div className="max-w-2xl animate-fadeup">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/40 bg-primary/10 text-primary text-xs font-semibold tracking-[0.2em] uppercase mb-6">
              <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" /> Map the gentle art
            </div>
            <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
              Build your<br /><span className="text-primary">Jiu-Jitsu</span> game,<br />move by move.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
              Create a living library of techniques with video, then chain them into visual flowcharts.
              See every option you have after each move — and train smarter.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link to="/register">
                <Button data-testid="hero-cta-btn" size="lg" className="bg-primary hover:bg-primary/90 text-white rounded h-12 px-7 text-base group">
                  Start building <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" size="lg" className="h-12 px-7 rounded border-white/20 bg-white/5 hover:bg-white/10 text-white">
                  I have an account
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="relative max-w-7xl mx-auto px-6 py-24">
        <p className="text-xs tracking-[0.25em] uppercase text-primary font-semibold mb-3">The system</p>
        <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight max-w-2xl">
          Everything your gym needs to document and drill the game.
        </h2>
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { icon: LibIcon, t: "Technique Library", d: "Add positions, submissions and escapes with video uploads or YouTube links." },
            { icon: Workflow, t: "Flowchart Builder", d: "Drag techniques onto a canvas and connect them with arrows to map your sequences." },
            { icon: PlayCircle, t: "Watch & Learn", d: "Every node plays its own video so you can study the exact details of each move." },
            { icon: Users, t: "Gym & Roles", d: "Admins and coaches curate the gym library. Members keep private techniques too." },
          ].map((f, i) => (
            <div key={i} className="group p-6 rounded-xl bg-card border border-border hover:border-primary/60 transition-colors duration-300">
              <f.icon className="h-7 w-7 text-primary mb-5" strokeWidth={2} />
              <h3 className="font-heading font-semibold text-lg mb-2">{f.t}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
        <div className="mt-12">
          <Link to="/how-it-works" className="inline-flex items-center gap-2 text-primary hover:underline font-medium" data-testid="features-how-it-works-link">
            Read how Infinite Sequence helps you learn faster <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <footer className="border-t border-border py-10">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between text-sm text-muted-foreground">
          <div className="flex items-center gap-2"><Hexagon className="h-4 w-4 text-primary" /> <span className="font-heading font-semibold text-white">INFINITE SEQUENCE</span></div>
          <div className="flex items-center gap-6">
            <Link to="/how-it-works" className="hover:text-white transition-colors" data-testid="footer-how-it-works-link">How it works</Link>
            <span>Train smarter. Flow harder.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
